import React, { useState, useEffect } from 'react';
import instituteLogo from './assets/Logo.png';
import Swal from 'sweetalert2'; 
import { db } from './Firebase';
import { collection, addDoc, setDoc, doc, query, orderBy, limit, getDocs, serverTimestamp } from 'firebase/firestore';

const initialFormData = {
  applicationId: '',
  gender: '',
  firstName: '',
  lastName: '',
  nickname: '',
  dob: '',
  religion: '',
  address: '',
  currentSchool: '',
  grade: '',
  fatherName: '',
  fatherAge: '',
  fatherOccupation: '',
  motherName: '',
  motherAge: '',
  motherOccupation: '',
  siblingsCount: '',
  sibling1Age: '',
  sibling2Age: '',
  sibling3Age: '',
  contact1: '',
  contact2: '',
  customerChannel: '',
  bookShippingAddress: '',
};

const formatDateInput = (value) => {
  let newValue = value.replace(/[^\d]/g, '');
  
  if (newValue.length > 8) {
    newValue = newValue.substring(0, 8);
  }
  
  if (newValue.length > 4) {
    newValue = newValue.substring(0, 2) + '-' + 
               newValue.substring(2, 4) + '-' + 
               newValue.substring(4);
  } else if (newValue.length > 2) {
    newValue = newValue.substring(0, 2) + '-' + 
               newValue.substring(2);
  }
  
  return newValue;
};

// 格式化电话号码的函数
const formatContactInput = (value) => {
  // 仅保留数字
  let digitsOnly = value.replace(/[^\d]/g, '');
  
  // 限制长度不超过10位
  if (digitsOnly.length > 10) {
    digitsOnly = digitsOnly.substring(0, 10);
  }
  
  // 如果数字少于3位，直接返回
  if (digitsOnly.length < 3) {
    return digitsOnly;
  }
  
  // 格式化为 XXX-XXXXXXX
  return digitsOnly.substring(0, 3) + '-' + digitsOnly.substring(3);
};

function StudentForm({ googleAppsScriptUrl }) {
  const [formData, setFormData] = useState(initialFormData);
  const [studentImage, setStudentImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingApplicationId, setIsLoadingApplicationId] = useState(false);

  const fetchNextApplicationId = async () => {
    try {
      setIsLoadingApplicationId(true);
      
      const q = query(collection(db, "student-register"), orderBy("ID", "desc"), limit(1));
      const querySnapshot = await getDocs(q);
      
      let nextId = 1;
      if (!querySnapshot.empty) {
        const lastDoc = querySnapshot.docs[0].data();
        if (lastDoc.ID) {
          nextId = Number(lastDoc.ID) + 1;
        }
      }

      setFormData(prev => ({
        ...prev,
        applicationId: nextId.toString()
      }));

    } catch (error) {
      console.error('Error fetching application ID from Firestore:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: `ไม่สามารถดึงเลขใบสมัครได้: ${error.message}`
      });
    } finally {
      setIsLoadingApplicationId(false);
    }
  };

  useEffect(() => {
    fetchNextApplicationId();
  }, []); // Remove googleAppsScriptUrl dependency as we use Firestore now

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'dob') {
      const formattedValue = formatDateInput(value);
      setFormData(prev => ({ ...prev, dob: formattedValue }));
      return;
    }
    
    // 格式化联系电话字段
    if (name === 'contact1' || name === 'contact2') {
      const formattedValue = formatContactInput(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'ขนาดไฟล์เกินกำหนด',
          text: 'ขนาดไฟล์รูปภาพต้องไม่เกิน 5MB'
        });
        e.target.value = '';
        return;
      }

      setStudentImage(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setStudentImage(null);
      setImagePreview(null);
    }
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1024;
          const MAX_HEIGHT = 1024;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // แปลงรูปภาพเป็น JPEG และบีบอัดคุณภาพลดลงเหลือ 70% เพื่อลดขนาดข้อมูลให้เล็กที่สุด
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          resolve(dataUrl.split(',')[1]);
        };
        img.onerror = error => reject(error);
      };
      reader.onerror = error => reject(error);
    });
  };

  const resetForm = async () => {
    setFormData(initialFormData);
    setStudentImage(null);
    setImagePreview(null);
    const fileInput = document.getElementById('studentImageFile');
    if (fileInput) {
      fileInput.value = '';
    }
    await fetchNextApplicationId();
  };

  const validateForm = () => {
    const requiredFields = [
      'applicationId', 
      'firstName', 
      'lastName', 
      'gender', 
      'religion', 
      'nickname', 
      'currentSchool', 
      'grade', 
      'contact1',
      'dob',
      'fatherName',
      'fatherAge',
      'fatherOccupation',
      'motherName',
      'motherAge',
      'motherOccupation'
    ]; 
    const missingFields = [];

    requiredFields.forEach(field => {
      if (!formData[field]?.toString().trim()) {
        missingFields.push(field);
      }
    });

    if (missingFields.length > 0) {
      const fieldNames = {
        applicationId: 'เลขที่ใบสมัคร',
        firstName: 'ชื่อ',
        lastName: 'นามสกุล',
        gender: 'เพศ',
        religion: 'ศาสนา',
        nickname: 'ชื่อเล่น',
        currentSchool: 'โรงเรียนที่เรียนอยู่ปัจจุบัน',
        grade: 'ระดับชั้น',
        contact1: 'เบอร์ติดต่อที่1',
        dob: 'วันเกิด',
        fatherName: 'ชื่อ-นามสกุลบิดา',
        fatherAge: 'อายุบิดา',
        fatherOccupation: 'อาชีพบิดา',
        motherName: 'ชื่อ-นามสกุลมารดา',
        motherAge: 'อายุมารดา',
        motherOccupation: 'อาชีพมารดา'
      };
      const missingFieldNames = missingFields.map(field => fieldNames[field] || field);
      
      Swal.fire({
        icon: 'error',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: `กรุณากรอกข้อมูลที่จำเป็น: ${missingFieldNames.join(', ')}`,
        confirmButtonColor: '#3085d6',
      });
      
      return false;
    }

    if (!/^\d{2}-\d{2}-\d{4}$/.test(formData.dob)) {
      Swal.fire({
        icon: 'error',
        title: 'รูปแบบวันเกิดไม่ถูกต้อง',
        text: 'กรุณากรอกในรูปแบบ DD-MM-YYYY (เช่น 01-01-2560)',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    const [day, month, year] = formData.dob.split('-').map(Number);
    const date = new Date(year - 543, month - 1, day);
    
    if (
      isNaN(date.getTime()) || 
      date.getDate() !== day || 
      (date.getMonth() + 1) !== month ||
      date.getFullYear() !== (year - 543)
    ) {
      Swal.fire({
        icon: 'error',
        title: 'วันเกิดไม่ถูกต้อง',
        text: 'กรุณาตรวจสอบวันที่ให้ถูกต้อง',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    if (year < 2500) {
      Swal.fire({
        icon: 'error',
        title: 'ปีที่กรอกไม่ถูกต้อง',
        text: 'ปีที่กรอกต้องเป็นพุทธศักราช (อย่างน้อย 2500 ขึ้นไป)',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    // 验证电话格式
    if (!/^[0-9-]{3,11}$/.test(formData.contact1)) {
      Swal.fire({
        icon: 'error',
        title: 'เบอร์โทรศัพท์ไม่ถูกต้อง',
        text: 'เบอร์โทรศัพท์ต้องอยู่ในรูปแบบ XXX-XXXXXXX',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    Swal.fire({
      title: 'กำลังบันทึกข้อมูล',
      html: 'กรุณารอสักครู่...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setIsSubmitting(true);

    try {
      const submissionData = { ...formData };
      let base64Image = null;

      if (studentImage) {
        try {
          base64Image = await convertImageToBase64(studentImage);
          submissionData.imageBase64 = base64Image;
          submissionData.imageMimeType = 'image/jpeg'; // รูปภาพถูกแปลงเป็น jpeg โดย canvas แล้ว
        } catch (error) {
          console.error('Image conversion error:', error);
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'เกิดข้อผิดพลาดในการแปลงรูปภาพ'
          });
          setIsSubmitting(false);
          return;
        }
      }

      // 1. Send to Google Apps Script first to get Image Link
      let imageLink = '';
      const formBody = Object.keys(submissionData)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(submissionData[key]))
        .join('&');

      try {
        const response = await fetch(googleAppsScriptUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
          },
          body: formBody
        });

        if (response.ok) {
          const resultText = await response.text();
          let result;
          
          try {
            result = JSON.parse(resultText);
          } catch (parseError) {
            console.error('JSON Parse Error:', parseError, "Response text:", resultText);
            
            // ดึงข้อความ error จาก HTML ที่ GAS ส่งกลับมา
            let errorMsg = 'Invalid response from server';
            if (resultText && resultText.includes('<title>Error</title>')) {
              errorMsg = 'Google Apps Script Error: ' + resultText.replace(/<[^>]+>/g, '').substring(0, 150);
            } else if (resultText) {
              errorMsg = 'Server response format error: ' + resultText.substring(0, 150);
            }
            throw new Error(errorMsg);
          }
          
          if (result.status === 'success') {
            // Get image link from GAS response if available
            // User's GAS returns it as 'driveFileLink'
            imageLink = result.imageLink || result.fileUrl || result.url || result.driveFileLink || '';
            console.log("Image Link from GAS:", imageLink);

            if (!imageLink && studentImage) {
              Swal.fire({
                icon: 'warning',
                title: 'ไม่พบลิงก์รูปภาพ',
                text: 'ระบบบันทึกข้อมูลได้ แต่ไม่ได้รับลิงก์รูปภาพจาก Google Drive (กรุณาตรวจสอบ Script)',
                timer: 5000
              });
            }

            // 2. Save to Firestore with the image link from GAS
            const firestoreData = {
              ID: Number(submissionData.applicationId),
              address: submissionData.address || '',
              birthday: submissionData.dob || '',
              'child-number': Number(submissionData.siblingsCount) || 0,
              'child1-age': Number(submissionData.sibling1Age) || 0,
              'child2-age': Number(submissionData.sibling2Age) || 0,
              'father- occupation': submissionData.fatherOccupation || '',
              'father-age': Number(submissionData.fatherAge) || 0,
              'father-name': submissionData.fatherName || '',
              'image-link': imageLink, // Use the link from GAS
              'mother- occupation': submissionData.motherOccupation || '',
              'mother-age': Number(submissionData.motherAge) || 0,
              name: submissionData.firstName || '',
              nickname: submissionData.nickname || '',
              religion: submissionData.religion || '',
              school: submissionData.currentSchool || '',
              'school-level': submissionData.grade || '',
              sex: submissionData.gender || '',
              surname: submissionData.lastName || '',
              telephone1: submissionData.contact1 || '',
              telephone2: submissionData.contact2 || '',
              timestamp: serverTimestamp()
            };

            try {
              await setDoc(doc(db, "student-register", String(submissionData.applicationId)), firestoreData);
              
              // If both successful:
              Swal.close();
              Swal.fire({
                icon: 'success',
                title: 'บันทึกข้อมูลสำเร็จ!',
                text: result.message || 'ข้อมูลถูกบันทึกเรียบร้อย',
                confirmButtonColor: '#3085d6',
              }).then(() => {
                resetForm();
              });

            } catch (firestoreError) {
              console.error("Firestore save error: ", firestoreError);
              throw new Error("Failed to save to database (Firestore)");
            }

          } else {
            // โยน error ที่มาจาก GAS ตรงๆ
            throw new Error(result.message || 'Google Apps Script returned an error');
          }
        } else {
          throw new Error(`Server returned status: ${response.status}`);
        }
      } catch (gasError) {
        console.error('GAS Submission error:', gasError);
        Swal.fire({
          icon: 'error',
          title: 'บันทึกข้อมูลล้มเหลว',
          text: gasError.message || 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้'
        });
      }
    } catch (error) {
      console.error('Submission error:', error);
      Swal.fire({
        icon: 'error',
        title: 'บันทึกข้อมูลล้มเหลว',
        text: error.message || 'เกิดข้อผิดพลาดที่ไม่คาดคิด'
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="student-form-container">
      <form onSubmit={handleSubmit} className="student-form">
        <header className="form-header">
          <img 
            src={instituteLogo} 
            alt="โลโก้สถาบัน" 
            className="institute-logo"
            style={{ width: '170px', height: '90px', marginBottom: '10px' }}
          />
          <h2>แบบฟอร์มลงทะเบียนนักเรียน</h2>
        </header>
        
        <div className="form-section">
          <div className="form-section-header">
            <h2>ข้อมูลนักเรียน</h2>
            <div className="form-group application-id-group">
              <label htmlFor="applicationId">เลขที่ใบสมัคร <span className="required">*</span></label>
              <div style={{ position: 'relative' }}>
                <input 
                  type="text" 
                  id="applicationId" 
                  name="applicationId" 
                  value={formData.applicationId} 
                  onChange={handleChange}
                  disabled={true}
                  style={{ 
                    backgroundColor: '#f8f9fa', 
                    cursor: 'not-allowed',
                    color: '#6c757d',
                    height: '40px'
                  }}
                  required 
                />
                {isLoadingApplicationId && (
                  <div style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px',
                    color: '#6c757d'
                  }}>
                    กำลังโหลด...
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="image-upload-section">
            <div className="image-preview-container">
              {!imagePreview ? (
                <div className="default-image-placeholder">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="60"
                    height="60"
                    fill="#888"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                  </svg>
                </div>
              ) : (
                <img src={imagePreview} alt="Preview" className="image-preview" />
              )}
            </div>

            <div className="upload-buttons">
              <button
                type="button"
                onClick={() => document.getElementById('studentImageFile').click()}
                className="upload-button"
                style={{ backgroundColor: '#2ecc71' }}
              >
                เลือกรูปภาพ
              </button>
            </div>
            <input
              type="file"
              id="studentImageFile"
              name="studentImageFile"
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="gender">เพศ <span className="required">*</span></label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
                style={{ width: '100%' }}
              >
                <option value="">-- เลือกเพศ --</option>
                <option value="ด.ช.">ด.ช.</option>
                <option value="ด.ญ.">ด.ญ.</option>
                <option value="นาย">นาย</option>
                <option value="นางสาว">นางสาว</option>
              </select>
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="nickname">ชื่อเล่น <span className="required">*</span></label>
              <input 
                type="text" 
                id="nickname" 
                name="nickname" 
                value={formData.nickname} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="firstName">ชื่อ <span className="required">*</span></label>
              <input 
                type="text" 
                id="firstName" 
                name="firstName" 
                value={formData.firstName} 
                onChange={handleChange} 
                required 
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="lastName">นามสกุล <span className="required">*</span></label>
              <input 
                type="text" 
                id="lastName" 
                name="lastName" 
                value={formData.lastName} 
                onChange={handleChange} 
                required 
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="dob">วัน-เดือน-ปี พ.ศ.เกิด (DD-MM-YYYY) <span className="required">*</span></label>
              <input 
                type="text"
                id="dob" 
                name="dob" 
                value={formData.dob} 
                onChange={handleChange}
                placeholder="DD-MM-YYYY (เช่น 01-01-2560)"
                required 
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="religion">ศาสนา <span className="required">*</span></label>
              <select
                id="religion"
                name="religion"
                value={formData.religion}
                onChange={handleChange}
                required
              >
                <option value="">-- เลือกศาสนา --</option>
                <option value="พุทธ">พุทธ</option>
                <option value="อิสลาม">อิสลาม</option>
                <option value="คริสต์">คริสต์</option>
                <option value="อื่นๆ">อื่นๆ</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ width: '100%' }}>
              <label htmlFor="address">ที่อยู่ <span className="required">*</span></label>
              <textarea 
                id="address" 
                name="address" 
                value={formData.address} 
                onChange={handleChange} 
                rows="3"
                required
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 2 }}>
              <label htmlFor="currentSchool">โรงเรียนที่เรียนอยู่ปัจจุบัน <span className="required">*</span></label>
              <input
                type="text"
                id="currentSchool"
                name="currentSchool"
                value={formData.currentSchool}
                onChange={handleChange}
                placeholder="กรุณากรอกชื่อโรงเรียน"
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="grade">ระดับชั้น <span className="required">*</span></label>
              <select
                id="grade"
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                required
              >
                <option value="">-- เลือกระดับชั้น --</option>
                <option value="อนุบาล1">อนุบาล 1</option>
                <option value="อนุบาล2">อนุบาล 2</option>
                <option value="อนุบาล3">อนุบาล 3</option>
                <option value="ประถมปีที่1">ประถมปีที่ 1</option>
                <option value="ประถมปีที่2">ประถมปีที่ 2</option>
                <option value="ประถมปีที่3">ประถมปีที่ 3</option>
                <option value="ประถมปีที่4">ประถมปีที่ 4</option>
                <option value="ประถมปีที่5">ประถมปีที่ 5</option>
                <option value="ประถมปีที่6">ประถมปีที่ 6</option>
                <option value="มัธยม1">มัธยม 1</option>
                <option value="มัธยม2">มัธยม 2</option>
                <option value="มัธยม3">มัธยม 3</option>
                <option value="มัธยม4">มัธยม 4</option>
                <option value="มัธยม5">มัธยม 5</option>
                <option value="มัธยม6">มัธยม 6</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-header">
            <h2>ข้อมูลผู้ปกครอง</h2>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="fatherName">ชื่อ-นามสกุลบิดา <span className="required">*</span></label>
              <input 
                type="text" 
                id="fatherName" 
                name="fatherName" 
                value={formData.fatherName} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="fatherAge">อายุบิดา <span className="required">*</span></label>
              <input 
                type="number" 
                id="fatherAge" 
                name="fatherAge" 
                value={formData.fatherAge} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="fatherOccupation">อาชีพบิดา <span className="required">*</span></label>
              <input 
                type="text" 
                id="fatherOccupation" 
                name="fatherOccupation" 
                value={formData.fatherOccupation} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="motherName">ชื่อ-นามสกุลมารดา <span className="required">*</span></label>
              <input 
                type="text" 
                id="motherName" 
                name="motherName" 
                value={formData.motherName} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="motherAge">อายุมารดา <span className="required">*</span></label>
              <input 
                type="number" 
                id="motherAge" 
                name="motherAge" 
                value={formData.motherAge} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="motherOccupation">อาชีพมารดา <span className="required">*</span></label>
              <input 
                type="text" 
                id="motherOccupation" 
                name="motherOccupation" 
                value={formData.motherOccupation} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="contact1">เบอร์ติดต่อที่1 <span className="required">*</span></label>
              <input 
                type="text" 
                id="contact1" 
                name="contact1" 
                value={formData.contact1} 
                onChange={handleChange}
                placeholder="XXX-XXXXXXX"
                pattern="\d{3}-\d{7}"
                title="เบอร์โทรศัพท์ต้องอยู่ในรูปแบบ XXX-XXXXXXX"
                style={{ width: '100%' }} 
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="contact2">เบอร์ติดต่อที่2</label>
              <input 
                type="text" 
                id="contact2" 
                name="contact2" 
                value={formData.contact2} 
                onChange={handleChange}
                placeholder="XXX-XXXXXXX"
                pattern="\d{3}-\d{7}"
                title="เบอร์โทรศัพท์ต้องอยู่ในรูปแบบ XXX-XXXXXXX"
                style={{ width: '100%' }} 
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="siblingsCount">จำนวนพี่น้อง</label>
              <input 
                type="number" 
                id="siblingsCount" 
                name="siblingsCount" 
                value={formData.siblingsCount || ''} 
                onChange={handleChange}
                style={{ width: '100%' }} 
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="sibling1Age">คนที่1อายุ</label>
              <input 
                type="number" 
                id="sibling1Age" 
                name="sibling1Age" 
                value={formData.sibling1Age} 
                onChange={handleChange}
                style={{ width: '100%' }} 
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="sibling2Age">คนที่2อายุ</label>
              <input 
                type="number" 
                id="sibling2Age" 
                name="sibling2Age" 
                value={formData.sibling2Age} 
                onChange={handleChange}
                style={{ width: '100%' }} 
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="sibling3Age">คนที่3อายุ</label>
              <input 
                type="number" 
                id="sibling3Age" 
                name="sibling3Age" 
                value={formData.sibling3Age} 
                onChange={handleChange}
                style={{ width: '100%' }} 
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={isSubmitting || isLoadingApplicationId} className="submit-button">
            {isSubmitting ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
          </button>

          <button type="button" onClick={resetForm} className="reset-button" disabled={isSubmitting || isLoadingApplicationId}>
            เคลียร์ฟอร์ม
          </button>
        </div>
      </form>
    </div>
  );
}

export default StudentForm;